/**
 * Página de Tabelas de Preços do CRM
 * Permite gerenciar tabelas de preços e seus itens
 * 
 * Versão: 4.0.0
 * - Layout padronizado com CrmCustomers (header compacto, MetricCards premium, filtros inline)
 * - Suporte a múltiplos workspaces por tabela de preço
 */
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table2,
  Plus,
  Edit,
  Trash2,
  MoreHorizontal,
  Star,
  Calendar,
  Package,
  Percent,
  Layers,
  Loader2,
  CheckCircle,
  Search,
  X,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { StatusDot } from '@/components/tenant/SettingsInfoRow';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  useCrmPriceTables,
  useCrmPriceTableItems,
  type CrmPriceTable,
  type CreatePriceTableInput,
} from '@/hooks/useCrmPriceTables';
import { useCrmProducts } from '@/hooks/useCrmProducts';
import { useCrmItemWorkspaces } from '@/hooks/useCrmItemWorkspaces';
import { WorkspaceMultiSelect } from '@/components/crm/WorkspaceMultiSelect';
import { CurrencyInput } from '@/components/ui/currency-input';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

/** Interface para estatísticas de tabelas de preços (KPIs) */
interface PriceTableStats {
  total_count: number;
  active_count: number;
  default_count: number;
  with_workspaces_count: number;
}

/** Props para o componente MetricCard */
interface MetricCardProps {
  title: string;
  value: number | string;
  icon: LucideIcon;
  variant?: 'primary' | 'success' | 'info' | 'purple';
}

// ============================================================================
// COMPONENTES AUXILIARES
// ============================================================================

/**
 * Estilos de variantes para KPI cards - padrão horizontal compacto
 */
const METRIC_CARD_STYLES: Record<string, { cardBg: string; iconColor: string }> = {
  primary: { cardBg: 'bg-purple-100 dark:bg-purple-900/30', iconColor: 'text-purple-700 dark:text-purple-300' },
  success: { cardBg: 'bg-lime-100 dark:bg-green-900/30', iconColor: 'text-green-700 dark:text-green-300' },
  info: { cardBg: 'bg-blue-100 dark:bg-blue-900/30', iconColor: 'text-blue-700 dark:text-blue-300' },
  purple: { cardBg: 'bg-cyan-100 dark:bg-cyan-900/30', iconColor: 'text-cyan-700 dark:text-cyan-300' },
};

/** Card de métrica compacto horizontal - padrão unificado CRM */
const MetricCard = ({ title, value, icon: Icon, variant = 'primary' }: MetricCardProps) => {
  const styles = METRIC_CARD_STYLES[variant] || METRIC_CARD_STYLES.primary;

  return (
    <Card className={cn('border-0 shadow-none transition-all hover:shadow-md', styles.cardBg)}>
      <CardContent className="p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className={cn('h-5 w-5 shrink-0', styles.iconColor)} />
            <p className="text-xs font-medium truncate text-gray-900 dark:text-gray-100">{title}</p>
          </div>
          <p className="text-xl font-bold leading-tight shrink-0 text-gray-900 dark:text-gray-100">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function CrmPriceTables() {
  const { t } = useTranslation();
  const { currentTenant } = useAuth();

  // === Controle de permissões granular ===
  const { canCreate, canEdit, canDelete } = usePermissions();
  const MENU_KEY = 'crm_prices';
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [itemsDialogOpen, setItemsDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState<CrmPriceTable | null>(null);
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [addItemDialogOpen, setAddItemDialogOpen] = useState(false);

  // Form state para tabela
  const [formData, setFormData] = useState<Partial<CreatePriceTableInput>>({
    name: '',
    description: '',
    is_default: false,
    valid_from: null,
    valid_until: null,
  });

  // State para workspaces selecionados (gerenciado separadamente)
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);

  // Form state para item
  const [itemFormData, setItemFormData] = useState({
    product_id: '',
    price: 0,
    min_quantity: 1,
    discount_percent: 0,
  });

  // Estados de filtro
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // Hooks
  const { priceTables, isLoading, refetch, createPriceTable, updatePriceTable, deletePriceTable } = useCrmPriceTables();
  const { items, createItem, deleteItem } = useCrmPriceTableItems(selectedTableId);
  const { products } = useCrmProducts();
  const { updateItemWorkspaces, fetchItemWorkspaces } = useCrmItemWorkspaces();

  // ============================================================================
  // FUNÇÕES AUXILIARES
  // ============================================================================

  /** Formatar preço em moeda brasileira */
  const formatPrice = (price: number, currency = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(price);
  };

  /** Formatar data para exibição */
  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return format(new Date(date), 'dd/MM/yyyy', { locale: ptBR });
  };

  // ============================================================================
  // HANDLERS DE TABELA
  // ============================================================================

  /** Abrir dialog para criar tabela */
  const handleCreate = () => {
    setEditingTable(null);
    setFormData({
      name: '',
      description: '',
      is_default: false,
      valid_from: null,
      valid_until: null,
    });
    setSelectedWorkspaceIds([]);
    setDialogOpen(true);
  };

  /** Abrir dialog para editar tabela */
  const handleEdit = async (table: CrmPriceTable) => {
    setEditingTable(table);
    setFormData({
      name: table.name,
      description: table.description || '',
      is_default: table.is_default,
      valid_from: table.valid_from,
      valid_until: table.valid_until,
    });
    // Carregar workspaces vinculados
    try {
      const wsIds = await fetchItemWorkspaces('price_table', table.id);
      setSelectedWorkspaceIds(wsIds);
    } catch {
      setSelectedWorkspaceIds(table.workspaces?.map((w) => w.id) || []);
    }
    setDialogOpen(true);
  };

  /** Salvar tabela (criar ou atualizar) */
  const handleSave = async () => {
    if (!formData.name) return;
    if (selectedWorkspaceIds.length === 0) {
      return; // Validação no componente
    }

    if (editingTable) {
      // Atualizar tabela existente
      await updatePriceTable.mutateAsync({
        id: editingTable.id,
        ...formData,
      });
      // Atualizar workspaces
      await updateItemWorkspaces.mutateAsync({
        itemType: 'price_table',
        itemId: editingTable.id,
        workspaceIds: selectedWorkspaceIds,
      });
    } else {
      // Criar nova tabela
      const newTable = await createPriceTable.mutateAsync(formData as CreatePriceTableInput);
      // Vincular workspaces
      await updateItemWorkspaces.mutateAsync({
        itemType: 'price_table',
        itemId: newTable.id,
        workspaceIds: selectedWorkspaceIds,
      });
    }
    setDialogOpen(false);
  };

  /** Deletar tabela */
  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta tabela de preços?')) {
      await deletePriceTable.mutateAsync(id);
    }
  };

  // ============================================================================
  // HANDLERS DE ITENS
  // ============================================================================

  /** Abrir dialog de gerenciamento de itens */
  const handleManageItems = (table: CrmPriceTable) => {
    setSelectedTableId(table.id);
    setEditingTable(table);
    setItemsDialogOpen(true);
  };

  /** Adicionar item à tabela */
  const handleAddItem = async () => {
    if (!selectedTableId || !itemFormData.product_id) return;

    await createItem.mutateAsync({
      price_table_id: selectedTableId,
      product_id: itemFormData.product_id,
      price: itemFormData.price,
      min_quantity: itemFormData.min_quantity,
      discount_percent: itemFormData.discount_percent,
    });

    setAddItemDialogOpen(false);
    setItemFormData({
      product_id: '',
      price: 0,
      min_quantity: 1,
      discount_percent: 0,
    });
  };

  /** Remover item da tabela */
  const handleRemoveItem = async (id: string) => {
    if (confirm('Remover este produto da tabela?')) {
      await deleteItem.mutateAsync(id);
    }
  };

  // ============================================================================
  // DADOS COMPUTADOS
  // ============================================================================

  /** Produtos disponíveis (não adicionados à tabela atual) */
  const availableProducts = products.filter(
    (p) => !items.some((item) => item.product_id === p.id)
  );

  /** Estatísticas das tabelas de preços */
  const stats = useMemo<PriceTableStats>(() => ({
    total_count: priceTables.length,
    active_count: priceTables.filter((t) => t.is_active).length,
    default_count: priceTables.filter((t) => t.is_default).length,
    with_workspaces_count: priceTables.filter((t) => t.workspaces && t.workspaces.length > 0).length,
  }), [priceTables]);

  /** Tabelas filtradas por busca e status */
  const filteredTables = useMemo(() => {
    let result = priceTables;
    
    // Filtro por status
    if (statusFilter === 'active') {
      result = result.filter(t => t.is_active);
    } else if (statusFilter === 'inactive') {
      result = result.filter(t => !t.is_active);
    }
    
    // Filtro por busca
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter((table) =>
        table.name.toLowerCase().includes(searchLower) ||
        table.description?.toLowerCase().includes(searchLower)
      );
    }
    
    return result;
  }, [priceTables, search, statusFilter]);

  /** Verificar se há filtros ativos */
  const hasActiveFilters = search || statusFilter !== 'all';

  /** Limpar todos os filtros */
  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="flex flex-col h-full">
      {/* Header Compacto */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
        <div>
          <h1 className="text-xl font-semibold">Tabelas de Preços</h1>
        </div>
        {hasCreatePermission && (
          <Button 
            size="xs" 
            onClick={handleCreate}
            className="hover:shadow-md hover:scale-[1.02] transition-all duration-200"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Nova Tabela
          </Button>
        )}
      </div>

      {/* KPI Cards */}
      {!isLoading && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3">
          <MetricCard
            title="Total de Tabelas"
            value={stats.total_count}
            icon={Table2}
            variant="primary"
          />
          <MetricCard
            title="Ativas"
            value={stats.active_count}
            icon={CheckCircle}
            variant="success"
          />
          <MetricCard
            title="Padrão"
            value={stats.default_count}
            icon={Star}
            variant="info"
          />
          <MetricCard
            title="Com Workspaces"
            value={stats.with_workspaces_count}
            icon={Layers}
            variant="purple"
          />
        </div>
      )}

      {/* Barra de Filtros */}
      <div className="flex flex-wrap items-center gap-2 p-3 border-b bg-muted/30">
        {/* Busca */}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>

        {/* Filtro de Status */}
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'all' | 'active' | 'inactive')}>
          <SelectTrigger className="w-[130px] h-8 text-sm">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="active">Ativas</SelectItem>
            <SelectItem value="inactive">Inativas</SelectItem>
          </SelectContent>
        </Select>

        {/* Botão Limpar */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
          >
            <X className="h-3 w-3 mr-1" />
            Limpar
          </Button>
        )}

        {/* Indicador de carregamento */}
        {isLoading && (
          <span className="text-xs text-muted-foreground animate-pulse">
            Atualizando...
          </span>
        )}
      </div>

      {/* Container da Tabela */}
      <div className="flex-1 overflow-auto">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : filteredTables.length === 0 ? (
          // Estado vazio compacto
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Table2 className="h-12 w-12 text-muted-foreground/40" />
            <div className="text-center">
              <h3 className="font-medium text-sm">
                {hasActiveFilters ? 'Nenhuma tabela encontrada' : 'Nenhuma tabela cadastrada'}
              </h3>
              <p className="text-xs text-muted-foreground mt-1">
                {hasActiveFilters ? 'Tente ajustar os filtros' : 'Crie sua primeira tabela de preços'}
              </p>
            </div>
            {!hasActiveFilters && hasCreatePermission && (
              <Button size="xs" onClick={handleCreate}>
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Nova Tabela
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-xs font-medium">Nome</TableHead>
                <TableHead className="text-xs font-medium">Workspace</TableHead>
                <TableHead className="text-xs font-medium">Validade</TableHead>
                <TableHead className="text-xs font-medium">Status</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTables.map((table) => (
                <TableRow key={table.id} className="hover:bg-muted/40 group">
                  <TableCell className="py-2">
                    <div className="flex items-center gap-2">
                      {table.is_default && (
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                      )}
                      <div>
                        <p className="font-medium text-sm">{table.name}</p>
                        {table.description && (
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {table.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    {table.workspaces && table.workspaces.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {table.workspaces.slice(0, 2).map((ws) => (
                          <Badge key={ws.id} variant="outline" className="text-xs">
                            <Layers className="h-3 w-3 mr-1" />
                            {ws.name}
                          </Badge>
                        ))}
                        {table.workspaces.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{table.workspaces.length - 2}
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">Sem workspace</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1 text-xs">
                      <Calendar className="h-3 w-3 text-muted-foreground" />
                      {table.valid_from || table.valid_until ? (
                        <span>
                          {formatDate(table.valid_from)} - {formatDate(table.valid_until)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Sem validade</span>
                      )}
                    </div>
                  </TableCell>
                  {/* Status — padrão unificado (Badge outline + StatusDot) */}
                  <TableCell className="py-2">
                    <Badge
                      variant="outline"
                      className="h-5 gap-1.5 px-1.5 text-[10px] font-medium uppercase tracking-wider"
                    >
                      <StatusDot tone={table.is_active ? 'emerald' : 'muted'} />
                      {table.is_active ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <MoreHorizontal className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {hasEditPermission && (
                          <DropdownMenuItem onClick={() => handleManageItems(table)}>
                            <Package className="h-4 w-4 mr-2" />
                            Gerenciar Produtos
                          </DropdownMenuItem>
                        )}
                        {hasEditPermission && (
                          <DropdownMenuItem onClick={() => handleEdit(table)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                        )}
                        {hasDeletePermission && (
                          <DropdownMenuItem
                            onClick={() => handleDelete(table.id)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Excluir
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Dialog de criação/edição de tabela */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTable ? 'Editar' : 'Nova'} Tabela de Preços
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Tabela Atacado"
              />
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Descrição opcional"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Workspaces *</Label>
              <WorkspaceMultiSelect
                value={selectedWorkspaceIds}
                onChange={setSelectedWorkspaceIds}
                placeholder="Selecione pelo menos um workspace..."
                required
              />
              <p className="text-xs text-muted-foreground">
                Selecione os workspaces onde esta tabela estará disponível
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Válido de</Label>
                <Input
                  type="date"
                  value={formData.valid_from?.split('T')[0] || ''}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value || null })}
                />
              </div>
              <div className="space-y-2">
                <Label>Válido até</Label>
                <Input
                  type="date"
                  value={formData.valid_until?.split('T')[0] || ''}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value || null })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Tabela Padrão</Label>
                <p className="text-sm text-muted-foreground">
                  Usar como padrão quando nenhuma outra for especificada
                </p>
              </div>
              <Switch
                checked={formData.is_default}
                onCheckedChange={(v) => setFormData({ ...formData, is_default: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.name || createPriceTable.isPending || updatePriceTable.isPending}
            >
              {editingTable ? 'Salvar' : 'Criar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de itens da tabela */}
      <Dialog open={itemsDialogOpen} onOpenChange={setItemsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Produtos - {editingTable?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => setAddItemDialogOpen(true)}
                disabled={availableProducts.length === 0}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Produto
              </Button>
            </div>

            {items.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>Nenhum produto nesta tabela</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead>Preço Original</TableHead>
                    <TableHead>Preço Tabela</TableHead>
                    <TableHead>Qtd. Mínima</TableHead>
                    <TableHead>Desconto</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.product?.name}</p>
                          {item.product?.sku && (
                            <code className="text-xs bg-muted px-1 rounded">
                              {item.product.sku}
                            </code>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatPrice(item.product?.default_price || 0)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatPrice(item.price)}
                      </TableCell>
                      <TableCell>{item.min_quantity}</TableCell>
                      <TableCell>
                        {item.discount_percent > 0 ? (
                          <Badge variant="outline" className="text-green-600">
                            <Percent className="h-3 w-3 mr-1" />
                            {item.discount_percent}%
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleRemoveItem(item.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setItemsDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para adicionar item */}
      <Dialog open={addItemDialogOpen} onOpenChange={setAddItemDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar Produto à Tabela</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Produto *</Label>
              <Select
                value={itemFormData.product_id}
                onValueChange={(v) => {
                  const product = products.find((p) => p.id === v);
                  setItemFormData({
                    ...itemFormData,
                    product_id: v,
                    price: product?.default_price || 0,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um produto" />
                </SelectTrigger>
                <SelectContent>
                  {availableProducts.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} - {formatPrice(product.default_price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Preço</Label>
                <CurrencyInput
                  value={itemFormData.price}
                  onChange={(value) => setItemFormData({ ...itemFormData, price: value })}
                  placeholder="R$ 0,00"
                />
              </div>
              <div className="space-y-2">
                <Label>Qtd. Mínima</Label>
                <Input
                  type="number"
                  min="1"
                  value={itemFormData.min_quantity}
                  onChange={(e) => setItemFormData({ ...itemFormData, min_quantity: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Desconto (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={itemFormData.discount_percent}
                  onChange={(e) => setItemFormData({ ...itemFormData, discount_percent: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddItemDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleAddItem}
              disabled={!itemFormData.product_id || createItem.isPending}
            >
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
